import React, { useCallback } from 'react'
import { useListState } from 'react-stately'
import { Key } from 'react-aria'
import { Flex } from '../../../core-components'
import { dynamicItemToItem } from '../base-select/dynamicItemUtils'
import { SelectListBase } from '../select-list/SelectListBase'
import { SelectItemDataProvider } from '../base-select/SelectItemContext'
import { useSelectListKeyboardDelegate } from '../select-list/useSelectListKeyboardDelegate'
import { useTimezoneListData } from './useTimezoneListData'

type Props = {
  hasTime?: boolean
  locale?: string
  onChange?: (value?: string) => void
  onSearchChange: (value: string) => void
  search?: string
  value?: string
  size?: 'M' | 'S'
} & (
  | {
      'aria-label': string
      'aria-labelledby'?: never
    }
  | {
      'aria-label'?: never
      'aria-labelledby': string
    }
)

export const TimezoneList = ({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  hasTime,
  locale,
  onChange,
  onSearchChange,
  search = '',
  value,
  size = 'M',
}: Props) => {
  const { isLoading, listData } = useTimezoneListData({ hasTime, locale, search, value, size })

  const state = useListState({
    children: dynamicItemToItem,
    disabledKeys: listData.disabledKeys,
    items: listData.options,
    selectedKeys: listData.existingValues.has(value as string) ? [value as string] : [],
    selectionMode: 'single',
    onSelectionChange: selection => {
      if (selection === 'all') {
        return onChange?.(listData.existingValues.values().next().value)
      }
      return onChange?.(
        selection.values().next().value ? String(selection.values().next().value) : undefined
      )
    },
  })
  const onFocusSelectListItem = useCallback(
    (key: Key) => {
      state.selectionManager.setFocused(true)
      state.selectionManager.setFocusedKey(key)
    },
    [state]
  )
  const keyboardDelegate = useSelectListKeyboardDelegate(state.collection, state.disabledKeys)

  return (
    <Flex maxHeight='inherit' minWidth={0} maxWidth={size === 'S' ? '340px' : undefined}>
      <SelectItemDataProvider value={listData.itemData}>
        <SelectListBase
          keyboardDelegate={keyboardDelegate}
          state={state}
          isLoading={isLoading}
          onSearchChange={onSearchChange}
          onFocusItem={onFocusSelectListItem}
          search={search}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
        />
      </SelectItemDataProvider>
    </Flex>
  )
}
